/**
 * OnboardingWizard
 *
 * Guided onboarding experience for Pro users setting up Bot Tracking.
 * 4-step wizard: Welcome → Add Site → Install Pixel → Verify
 */

import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import { Telescope, Globe, Code, CheckCircle, Check, Copy } from 'lucide-react';
import { FEATURED_BOTS, BOT_PROFILES } from '@/data/botProfiles';
import { DsIsland } from '@/design-system/island';
import { Button, Dialog, Field, Input, Notice, Select, Steps } from '@/design-system/primitives';
import '@/design-system/botsapp.css';
import { useEngagementTracking } from '@/hooks/useEngagementTracking';
import '@/design-system/form.css'

interface OnboardingWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (siteId: string) => void;
  /** Skip the welcome step and go directly to add-site (useful when coming from WelcomeScreen) */
  skipWelcome?: boolean;
  /** Domain-scoped setup locks bot tracking to an existing account domain. */
  lockedDomain?: string;
}

type Step = 'welcome' | 'add-site' | 'install' | 'verify';

const PLATFORMS = [
  { value: 'hubspot', label: 'HubSpot', popular: true },
  { value: 'shopify', label: 'Shopify', popular: true },
  { value: 'wordpress', label: 'WordPress', popular: false },
  { value: 'webflow', label: 'Webflow', popular: false },
  { value: 'custom', label: 'Custom/HTML', popular: false },
];

const STEP_CONFIG: Record<Step, { number: number; title: string; icon: React.ElementType }> = {
  welcome: { number: 0, title: 'Welcome', icon: Telescope },
  'add-site': { number: 1, title: 'Add Website', icon: Globe },
  install: { number: 2, title: 'Install Pixel', icon: Code },
  verify: { number: 3, title: 'Verify', icon: CheckCircle },
};

export function OnboardingWizard({
  open,
  onOpenChange,
  onComplete,
  skipWelcome = false,
  lockedDomain,
}: OnboardingWizardProps) {
  const [step, setStep] = useState<Step>(skipWelcome ? 'add-site' : 'welcome');
  const [domain, setDomain] = useState('');
  const [platform, setPlatform] = useState('hubspot');
  const [copied, setCopied] = useState(false);
  const trackEngagement = useEngagementTracking();
  const [createdSite, setCreatedSite] = useState<{
    id: string;
    publicSiteId: string;
    domain: string;
  } | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep(skipWelcome ? 'add-site' : 'welcome');
      setDomain(lockedDomain || '');
      setPlatform('hubspot');
      setCopied(false);
      setCreatedSite(null);
    }
  }, [open, skipWelcome, lockedDomain]);

  // Create site mutation
  const createSiteMutation = useMutation({
    mutationFn: (data: { domain: string; platform: string }) =>
      apiService.createAbaSite(data),
    onSuccess: (response) => {
      // apiService returns the response data directly, so access response.site
      const site = response.site;
      if (site) {
        setCreatedSite({
          id: site.id,
          publicSiteId: site.publicSiteId,
          domain: site.domain,
        });
        setStep('install');
      }
    },
  });

  // Verify site mutation
  const verifySiteMutation = useMutation({
    mutationFn: (siteId: string) => apiService.verifyAbaSite(siteId),
  });

  const workerUrl = import.meta.env.VITE_ABA_WORKER_URL || 'https://track.superschema.workers.dev';

  const getTrackingCode = () => {
    if (!createdSite) return '';
    const pixelUrl = `${workerUrl}/pixel/${createdSite.publicSiteId}`;

    if (platform === 'hubspot') {
      return `<!-- SuperSchema AI Bot Tracking -->
<script>
document.addEventListener('DOMContentLoaded', function() {
  var img = document.createElement('img');
  img.src = '${pixelUrl}?path=' + encodeURIComponent(window.location.pathname);
  img.width = 1;
  img.height = 1;
  img.style.cssText = 'position:absolute;left:-9999px;';
  img.alt = '';
  document.body.appendChild(img);
});
</script>`;
    }

    if (platform === 'shopify') {
      return `<!-- SuperSchema AI Bot Tracking -->
<script>
(function(){
  var img = document.createElement('img');
  img.src = '${pixelUrl}?u=' + encodeURIComponent(window.location.href) + '&t=' + encodeURIComponent(document.title || '');
  img.width = 1;
  img.height = 1;
  img.style.cssText = 'position:absolute;left:-9999px;';
  img.alt = '';
  document.body.appendChild(img);
})();
</script>`;
    }

    return `<!-- SuperSchema AI Bot Tracking -->
<img src="${pixelUrl}" width="1" height="1" style="position:absolute;left:-9999px;" alt="" />`;
  };

  const getInstallInstructions = () => {
    switch (platform) {
      case 'hubspot':
        return [
          'Go to Settings in your HubSpot portal',
          'Navigate to Website → Pages',
          'Click "Site Footer HTML"',
          'Paste the code and click Save',
        ];
      case 'shopify':
        return [
          'Go to Online Store → Themes in your Shopify admin',
          'Click "..." next to your theme → Edit code',
          'Open Layout → theme.liquid',
          'Paste the code just before </body>',
          'Click Save',
        ];
      case 'wordpress':
        return [
          'Go to Appearance → Theme File Editor',
          'Open footer.php or use a header/footer plugin',
          'Paste the code before </body>',
          'Save changes',
        ];
      case 'webflow':
        return [
          'Go to Project Settings → Custom Code',
          'Paste in the Footer Code section',
          'Save and publish your site',
        ];
      default:
        return [
          'Open your website\'s HTML files',
          'Find the closing </body> tag',
          'Paste the code just before it',
          'Save and upload the file',
        ];
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(getTrackingCode());
    setCopied(true);
    trackEngagement('bot_tracking_pixel_copied', {
      domain: createdSite?.domain || lockedDomain || domain,
      source: 'onboarding_wizard',
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreateSite = () => {
    const cleanDomain = (lockedDomain || domain)
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '');

    createSiteMutation.mutate({ domain: cleanDomain, platform });
  };

  const handleVerify = () => {
    if (createdSite) {
      verifySiteMutation.mutate(createdSite.id);
    }
  };

  const handleComplete = () => {
    if (createdSite) {
      onComplete(createdSite.id);
      onOpenChange(false);
    }
  };

  if (!open) return null;

  const STEP_LABELS = ['The site', 'The snippet', 'The check'];
  const currentStepNumber = STEP_CONFIG[step].number;

  const title =
    step === 'welcome'
      ? 'Set up bot tracking'
      : step === 'add-site'
        ? 'Which site should we watch?'
        : step === 'install'
          ? 'Put this on your pages'
          : 'Let us check it is there';

  const lead =
    step === 'welcome'
      ? 'See which AI systems read your site, and which pages they open.'
      : step === 'add-site'
        ? 'And what it is built on, so we hand you the right instructions.'
        : step === 'install'
          ? 'One block of code, on every page you want watched.'
          : 'We fetch a page and look for the snippet.';

  const actions =
    step === 'welcome' ? (
      <Button onClick={() => setStep('add-site')}>Start setup</Button>
    ) : step === 'add-site' ? (
      <>
        {!skipWelcome && (
          <Button variant="secondary" onClick={() => setStep('welcome')}>
            Back
          </Button>
        )}
        <Button
          onClick={handleCreateSite}
          disabled={!domain.trim() || createSiteMutation.isPending}
          loading={createSiteMutation.isPending}
        >
          {createSiteMutation.isPending ? 'Adding' : 'Continue'}
        </Button>
      </>
    ) : step === 'install' ? (
      <>
        <Button variant="secondary" onClick={() => setStep('add-site')}>
          Back
        </Button>
        <Button onClick={() => setStep('verify')}>I have added it</Button>
      </>
    ) : (
      <>
        <Button variant="ghost" onClick={handleComplete}>
          {verifySiteMutation.data?.verified ? 'Done' : 'Skip for now'}
        </Button>
        {!verifySiteMutation.data?.verified && (
          <Button
            onClick={handleVerify}
            disabled={verifySiteMutation.isPending}
            loading={verifySiteMutation.isPending}
          >
            {verifySiteMutation.isPending ? 'Looking' : 'Check now'}
          </Button>
        )}
      </>
    );

  return (
    <DsIsland panel={false} ground={false}>
      <Dialog
        open
        size="lg"
        onClose={() => onOpenChange(false)}
        title={title}
        lead={lead}
        actions={actions}
      >
        <div data-testid="bot-tracking-onboarding-modal" className="ds-botwiz">
          {/* Three steps, and which one you are on. */}
          <Steps steps={STEP_LABELS} current={Math.max(0, currentStepNumber - 1)} />

          {step === 'welcome' && (
            <div className="ds-botwiz__body">
              <p className="ds-botwiz__prose">
                We watch for {FEATURED_BOTS.slice(0, 3).map((id) => BOT_PROFILES[id]?.name).filter(Boolean).join(', ')} and
                about twenty more. Nothing is recorded about your visitors: this counts
                machines, not people.
              </p>
              <p className="ds-botwiz__prose">
                It takes about two minutes: tell us the site, put one line on your pages, and
                we check we can see it.
              </p>
            </div>
          )}

          {step === 'add-site' && (
            <div className="ds-fields">
              <Field
                label="Website domain"
                hint={
                  lockedDomain
                    ? 'Bot tracking is tied to this account domain. To track a different site, add it as a domain first.'
                    : 'Just the domain, like example.com'
                }
              >
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    readOnly={!!lockedDomain}
                    placeholder="example.com"
                    autoFocus={!lockedDomain}
                  />
                )}
              </Field>

              <Field label="What it is built on" hint="This decides which install instructions you get.">
                {(id, describedBy) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {createSiteMutation.isError && (
                <Notice kind="look">
                  We could not add that site. Check the address and try again.
                </Notice>
              )}
            </div>
          )}

          {step === 'install' && (
            <div className="ds-botwiz__body">
              <ol className="ds-botwiz__steps">
                {getInstallInstructions().map((line, index) => (
                  <li key={line}>
                    <span className="ds-botwiz__n" aria-hidden="true">
                      {index + 1}
                    </span>
                    <span>{line}</span>
                  </li>
                ))}
              </ol>

              <div className="ds-botcode">
                <pre className="ds-botcode__block">{getTrackingCode()}</pre>
                <div className="ds-botcode__act">
                  <Button variant="secondary" size="sm" onClick={handleCopy}>
                    {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
                    {copied ? 'Copied' : 'Copy the snippet'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === 'verify' && (
            <div className="ds-botwiz__body">
              {verifySiteMutation.data?.verified ? (
                <Notice kind="good">
                  Found it. We are watching {createdSite?.domain} from now on, and a first
                  visit usually lands within a day or two.
                </Notice>
              ) : verifySiteMutation.isSuccess ? (
                <Notice kind="look">
                  Not there yet. A cached page is the usual reason, so it is worth another try
                  in half a minute. You can also skip this and check later.
                </Notice>
              ) : (
                <p className="ds-botwiz__prose">
                  We will fetch a page from {createdSite?.domain} and look for the snippet.
                  If it is not there yet you can skip, and check again from the Setup tab.
                </p>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </DsIsland>
  );
}
